import {
  SkeletonLoader,
  SmartTableColumn,
  WithNAFallback,
} from '@toggle/design-system';
import { formatPercentage, getCurrencySymbol } from '@toggle/helpers';
import { FinancialData, formatTicker } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { formatNumberWithUnit } from '~/widgets/earnings/utils/utils';
import { RatingPill } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/rating-pill/RatingPill';
import { SharedTable } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/shared-table/SharedTable';

import { EarningsPill } from '../EarningsPill';
import { CompanyCardProps } from './company-card-types';
import styles from './CompanyCard.module.scss';

export const PrimaryCompanyCard = ({
  company,
  entity,
  hideRelationship = false,
}: CompanyCardProps) => {
  const { t } = useTranslation(['earnings']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const earningsId = company.earnings_id;
  const { data: earningsData, isLoading } = useGetEarningById(earningsId);

  const reportingDate = earningsData?.reporting_date;
  const isReported = !!earningsData?.reported;
  const eps = earningsData?.eps;
  const revenue = earningsData?.revenue;

  if (isLoading) {
    return (
      <div className={styles.loaderRoot}>
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '100px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      </div>
    );
  }

  const tableData = [
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.eps'),
      key: 'eps',
      ...eps,
    },
    {
      name: t('earnings:earningsInsight.drawer.earningsPreview.tables.revenue'),
      key: 'revenue',
      ...revenue,
    },
  ];

  const tableColumns: SmartTableColumn<
    FinancialData & { name: string; key: string }
  >[] = [
    {
      label: t('earnings:earningsInsight.drawer.earningsPreview.tables.name'),
      key: 'name',
      renderHeader: () => {
        return null;
      },
      render: value => <div className={styles.tableNameCell}>{value}</div>,
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.estimate'
      ),
      key: 'estimate',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(entity.currency);
        return (
          <WithNAFallback
            check={value !== undefined && value !== null}
            value={
              <div className={styles.numberCell}>
                {`${currencySymbol}${formatNumberWithUnit(value)}`}
              </div>
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.reported'
      ),
      key: 'value',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(entity.currency);
        return (
          <WithNAFallback
            check={value !== undefined && value !== null}
            value={
              <div className={styles.numberCell}>
                {`${currencySymbol}${formatNumberWithUnit(value)}`}
              </div>
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.surprise'
      ),
      key: 'surprise',
      alignment: 'right',
      render: value => {
        const currencySymbol = getCurrencySymbol(entity.currency);
        return (
          <WithNAFallback
            value={
              value !== undefined && (
                <div
                  className={clsx(
                    styles.surpriseCell,
                    value > 0 && styles.surpriseCellPositive,
                    value === 0 && styles.surpriseCellZero
                  )}
                >
                  {`${currencySymbol}${formatNumberWithUnit(value)}`}
                </div>
              )
            }
          />
        );
      },
    },
    {
      label: t(
        'earnings:earningsInsight.drawer.earningsPreview.tables.changeYoy'
      ),
      key: 'estimate_yoy_percentage',
      alignment: 'right',
      render: value => (
        <WithNAFallback
          value={
            value !== undefined && (
              <div
                className={clsx(
                  styles.surpriseCell,
                  value > 0 && styles.surpriseCellPositive,
                  value === 0 && styles.surpriseCellZero
                )}
              >
                {formatPercentage(value, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </div>
            )
          }
        />
      ),
    },
  ];

  return (
    <div
      className={styles.root}
      onClick={() => navigateToPage({ entityTag: company.entity_tag })}
    >
      <div className={styles.headerWrapper}>
        <div className={styles.assetDetailsWrapper}>
          <AssetLogoContainer size={40} entity={entity} />
          <div className={styles.assetNameWrapper}>
            <span className={styles.assetNameLabel}>
              {entity.name_short || entity.name}
            </span>
            <span className={styles.assetTickerLabel}>
              {formatTicker(entity)}
            </span>
          </div>
        </div>
        <RatingPill rating={company.rating} />
      </div>
      {!!reportingDate && (
        <EarningsPill
          entity={entity}
          isReported={isReported}
          reportingDate={reportingDate}
        />
      )}
      {isReported ? (
        <SharedTable
          tableData={tableData}
          tableColumns={tableColumns}
          noCellPadding
        />
      ) : (
        <div className={styles.potentialImpactLabel}>
          {t('earnings:earningsInsight.drawer.earningsPreview.potentialImpact')}
        </div>
      )}
      <div
        className={clsx(
          styles.descriptionLabel,
          isReported && styles.descriptionLabelReported
        )}
      >
        {company.description}
      </div>
      {!hideRelationship && company.relationship && (
        <div className={styles.relationLabel}>{`${t(
          'earnings:earningsInsight.drawer.earningsPreview.relationship'
        )} ${company.relationship}`}</div>
      )}
    </div>
  );
};
